import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  untracked,
} from '@angular/core';
import {TranslationService} from "../../../../core/services/translation.service";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {CssPropertiesEnum} from "../../../../core/models/css-properties.enum";
import {CssPropertyPipe} from "../../../../core/pipes/css-property.pipe";
import {VHostService} from "../../../../core/services/v-host.service";
import {GeneralWarningComponent} from "../../../../core/components/general-warning/general-warning.component";
import {SubExtendedDataField} from "../../../../data/models/sub-extended-data-field";
import {ValidationPattern} from "../../../../core/models/ValidationPattern";
import {MatError, MatFormField, MatHint, MatInput, MatLabel} from "@angular/material/input";
import {FormOfPayment} from "../../../../data/models/formOfPayment.model";
import {ImageService} from "../../../../core/services/image.service";
import {BaseData} from "../../../../data/models/base-data.model";
import {GtmEnum} from "../../../../core/models/gtm.enum";
import {NgOptimizedImage} from "@angular/common";
import {MatOption, MatSelect} from "@angular/material/select";
import {LoadingSpinnerComponent} from "../../../../core/components/loading-spinner/loading-spinner.component";
import {OfferPrice} from "../../../../data/models/price.model";
import {PaymentProvider, PaymentTransactionModel} from "../../../../core/components/onlinepayment/paymentTransaction.model";
import {OrderTrackingService} from "../../../../core/services/order-tracking.service";
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {Offer} from "../../../../data/models/offer.model";
import {UserData} from "../../../../data/models/user-data.model";
import {isDigital} from "../../../../core/utils/offer-utils";
import {HupSubscriptionsService} from "../../../../data/services/hup-subscriptions.service";
import {ConfigService} from "../../../../core/services/config.service";
import {MatButton} from "@angular/material/button";
import {PaymentProviderService} from "../../../../core/services/payment-provider.service";
import {BaseDataService} from "../../../../core/services/base-data.service";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatStepper} from "@angular/material/stepper";
import {ImageHandler} from "../../../../core/services/image-handler.service";
import {PaymentService} from "../../../../data/services/payment.service";
import {CustomValidators} from "../../../../core/validators";
import {HttpClient} from "@angular/common/http";
import {PaymentFormValue} from "../../../../data/models/subscription-order.model";
import {OnlinepaymentComponent} from "../../../../core/components/onlinepayment/onlinepayment.component";
import {debounceTime, distinctUntilChanged, map, Subscription} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {
  isOnlinePaymentMethod,
  isPaymentStepComplete,
  isPaymentStepNavigable,
  OnlinePaymentCodes,
  PaymentStepState
} from "../../../../core/utils/payment-step-completion";

@Component({
  selector: 'app-payment-step',
  imports: [
    CssPropertyPipe,
    ReactiveFormsModule,
    GeneralWarningComponent,
    MatFormField,
    MatLabel,
    MatError,
    NgOptimizedImage,
    MatSelect,
    MatOption,
    MatHint,
    LoadingSpinnerComponent,
    MatButton,
    MatCheckbox,
    MatDatepicker,
    MatInput,
    MatDatepickerToggle,
    MatDatepickerInput,
    OnlinepaymentComponent
  ],
  templateUrl: './payment-step.component.html',
  styleUrl: './payment-step.component.scss'
})
export class PaymentStepComponent implements OnInit, OnDestroy {
  protected readonly CssPropertiesEnum = CssPropertiesEnum;
  protected readonly document = typeof document === 'object' ? document : null; // doc n.a. in SSR


  @Output() readyState = new EventEmitter<boolean>();
  @Output() onlinePaymentSelected = new EventEmitter<boolean>;

  //Services
  protected translationService = inject(TranslationService);
  protected orderTrackingService = inject(OrderTrackingService);
  protected imageService = inject(ImageService);
  protected vHostService = inject(VHostService);
  protected http = inject(HttpClient);

  private orderProcessService = inject(OrderProcessingService);
  private subscriptionService = inject(HupSubscriptionsService);
  private configService = inject(ConfigService);
  private paymentTransactionService = inject(PaymentProviderService);
  private baseDataService = inject(BaseDataService);
  private imageHandler = inject(ImageHandler);
  private paymentService = inject(PaymentService);
  private route = inject(ActivatedRoute);

  @Input() parentStepper!: MatStepper
  public currentProductAlreadyOrdered = input<boolean>();
  public offer = input<Offer>();
  public userData = input<UserData>();

  isDigitalOffer(): boolean {
    return isDigital(this.offer()?.offerType);
  }

  private formBuilder = inject(FormBuilder);
  protected paymentFormGroup: FormGroup;

  protected formsOfPayment: FormOfPayment[] = [];
  protected allFormsOfPayment: BaseData[] = [];
  protected paymentFrequencies: BaseData[] = [];
  protected allPaymentFrequencies: BaseData[] = [];
  protected additionalData: SubExtendedDataField[] = [];
  protected selectedFormOfPayment = null;
  protected isOnlinePayment = signal(false);

  protected needIbanBic: boolean = false;
  protected askForPaymentAccountHolder: boolean = false;

  protected needInvoiceMailAddress = signal(false);
  protected showSEPABox = signal(false);
  protected askForEditionId = signal(false);

  protected minDate = signal(new Date());
  protected maxDate = signal(null);

  protected calculatingPrice = false;
  protected priceCalculationError = false;

  protected offerPrice = signal<OfferPrice[]>(null);

  protected variantCodeDescriptions: Map<string, string> = new Map();
  protected selectedStartDate = signal(new Date());

  protected checkBoxes = signal(false); //todo: why is it here?

  protected onlinePaymentSuccess = signal(false);
  private autoNavigatedAfterOnlinePayment = false;

  /** Ein Klick auf "Weiter" wartet noch auf das Ergebnis der Preisberechnung. */
  private navigateWhenPriceArrives = false;

  /**
   * Ob der Nutzer den Zahlungs-Schritt für die aktuelle Zahlart abgeschlossen, also die
   * Preisermittlung angestoßen hat. Ein Zahlartwechsel entwertet das: kommt der Nutzer von der
   * Zusammenfassung zurück und wählt eine andere Zahlart, muss er den Schritt erneut abschließen,
   * bevor er über den Stepper-Header wieder vorblättern kann.
   */
  private priceConfirmedForSelection = false;
  private subscriptions = new Subscription();
  // Zuletzt aus dem Registrierungs-Schritt übernommene E-Mail. Solange die Rechnungs-E-Mail damit
  // identisch ist (oder leer), wird sie weiter mitgeführt; weicht der Nutzer ab, bleibt seine Eingabe.
  private lastSyncedRegistrationEmail: string = null;

  private readonly OFFER_ID_KEY = 'OfferId: ';
  private readonly SSO_ID_KEY = ', User-SSO-Id: ';
  private readonly WEBABO_TRANSACTION_ID_KEY = ', Webabo-Transaction-Id: ';

  // Die UserData werden von der Eltern-Komponente asynchron nachgeladen. Bei Angeboten mit nur einer
  // Zahlart wird diese bereits in ngOnInit automatisch ausgewählt - die Online-Transaktion entsteht
  // dann ggf. bevor die SSO-Id vorliegt und trägt dauerhaft "User-SSO-Id: 0". Deshalb die Transaktion
  // neu aufbauen, sobald die UserData eintreffen. untracked(), damit ausschließlich userData die
  // Abhängigkeit ist und nicht die in prepareOnlinePaymentTransaction gelesenen Signale.
  private readonly userDataChanged = effect(() => {
    this.userData();
    untracked(() => this.prepareOnlinePaymentTransaction());
  });

  ngOnInit(): void {
    this.buildPaymentForm();
    this.handleBaseData();
    this.handleConfigs();

    if (this.offer()) {
      this.handleFormsOfPayment(this.offer());
      this.handleVariantCodes(this.offer());
      this.handleRequiredAdditionalData();
    }

    this.observeStatusChanges();

    if (this.offer()) {
      this.handleStartDateRange(this.offer());
    }

    // Gibt es nur eine Zahlart, automatisch auswählen - erst hier, nachdem die valueChanges-
    // Subscription steht, damit es exakt wie ein Klick auf die Zahlart-Kachel wirkt (setzt IBAN,
    // Zahlrhythmus, Rechnungs-E-Mail usw.).
    this.setUniquePaymentInfos();

    if (this.currentProductAlreadyOrdered()) {
      this.skipDirectlyToLastStep();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private observeStatusChanges() {
    this.subscriptions.add(this.paymentFormGroup.statusChanges
      .pipe(
        debounceTime(500)
      )
      .subscribe(() => this.emitReadyState()));

    this.subscriptions.add(this.paymentFormGroup.get('startdate').valueChanges.subscribe(value => {
      this.selectedStartDate.set(value);
    }));

    this.subscriptions.add(this.paymentFormGroup.get('formOfPayment').valueChanges.subscribe(formOfPayment => {
      this.handleFormOfPaymentChange(formOfPayment);
    }));

    this.subscriptions.add(this.orderProcessService.connectForm<PaymentFormValue>(
      this.paymentFormGroup,
      state => state,
      (state, value) => ({
        ...state,
        payment: value
      })
    ));

    // E-Mail aus dem Registrierungs-Schritt in die Rechnungs-E-Mail übernehmen, sobald sie verfügbar
    // ist. Wichtig für Angebote mit nur einer Zahlart ("Rechnung per E-Mail"): diese wird automatisch
    // ausgewählt, bevor der Nutzer im 1. Schritt die E-Mail eingegeben hat - das Vorbelegen in
    // handleFormOfPaymentChange greift dann ins Leere. Eine vom Nutzer manuell eingegebene
    // Rechnungs-E-Mail wird nicht überschrieben (nur befüllen, wenn das Feld noch leer ist).
    this.subscriptions.add(this.orderProcessService.getOrderState()
      .pipe(
        map(state => state?.registration?.email || state?.ssoEmail),
        distinctUntilChanged()
      )
      .subscribe(email => {
        if (!this.needInvoiceMailAddress() || !email) {
          return;
        }
        const invoiceEmailControl = this.paymentFormGroup.get('invoiceEmail');
        // Mitführen, solange der Nutzer die Rechnungs-E-Mail nicht selbst abweichend geändert hat:
        // Feld leer ODER noch identisch mit der zuletzt übernommenen Registrierungs-E-Mail.
        // (Beim zeichenweisen Tippen im 1. Schritt wird so die komplette E-Mail übernommen.)
        if (!invoiceEmailControl.value || invoiceEmailControl.value === this.lastSyncedRegistrationEmail) {
          invoiceEmailControl.patchValue(email);
          this.lastSyncedRegistrationEmail = email;
        }
      }));

    // Initialwerte (z.B. automatisch vorbelegte Zahlart/Frequenz/Startdatum) direkt in den State schreiben.
    this.orderProcessService.update(state => ({
      ...state,
      payment: this.paymentFormGroup.getRawValue() as PaymentFormValue
    }));

    // Erst jetzt steht die Zahlung im Bestell-State. Bei einer automatisch vorgewaehlten
    // Online-Zahlart lief der Aufruf in handleFormOfPaymentChange noch ins Leere - hier wird der
    // Preis nachgeholt, damit der Bezahl-Button nicht mit dem Ersatzpreis aufgebaut wird.
    if (this.isOnlinePayment()) {
      this.calcPriceOfSubscription();
    }
  }

  private handleRequiredAdditionalData() {
    if (this.offer().requiredAdditionalData) {
      this.offer().requiredAdditionalData.forEach(
        extendedData => {
          extendedData.dataSets.forEach(
            dataSet => {
              const control = new FormControl(dataSet.value);
              dataSet.itemkey = extendedData.itemkey;
              (this.paymentFormGroup.get('extendedData') as FormArray).push(control);
              this.additionalData.push(dataSet);

              this.orderProcessService.update(state => ({
                ...state,
                additionalData: this.additionalData
              }));
            }
          );
        }
      );
    }
  }

  private handleBaseData() {
    this.allPaymentFrequencies = this.baseDataService.getBaseData('invoicemethods');
    this.allFormsOfPayment = this.baseDataService.getBaseData('paymentmethods');
  }

  private handleConfigs() {
    this.askForPaymentAccountHolder = this.configService.getConfig('askForPaymentAccountHolder').value === '1';
   this.configService.loadConfig('order.showSEPABox').subscribe(config => {
      if (config && config.value) {
        this.showSEPABox.set(config.value === 'true' || config.value === '1');
      }
    });

    this.configService.loadConfig('offer.askForEditionId').subscribe(config => {
      if (config && config.value) {
        this.askForEditionId.set(config.value === '1');
      }
    });
  }

  /**
   * Ermittelt das früheste wählbare Startdatum: bei Print-Angeboten den nächsten Liefertermin aus dem
   * Kalender (ggf. verschoben um order.delayToStart.<Wochentag>), bei Digital-Angeboten jetzt. Ein per
   * Query-Parameter "startDate" übergebenes Datum gilt, wenn es später liegt. Das Backend lehnt Bestellungen
   * mit einem Startdatum vor dem nächsten Liefertermin ab (SubscriptionValidator, Feld "validFrom").
   */
  private handleStartDateRange(offer: Offer) {
    const startDateUrl = this.route.snapshot.queryParamMap.get('startDate');
    this.raiseMinDate(startDateUrl ? new Date(startDateUrl) : new Date());

    if (isDigital(offer.offerType)) {
      return;
    }

    this.subscriptions.add(this.subscriptionService.getNextDeliveryDate(offer.product.productCode, offer.product.variantCodeList[0]).subscribe({
      next: nextDeliveryDate => {
        const delayedDate = new Date();
        this.subscriptions.add(this.configService.loadConfig('order.delayToStart.' + delayedDate.getDay()).subscribe(config => {
          if (config?.value != null) {
            delayedDate.setDate(delayedDate.getDate() + parseInt(config.value));
          }
          this.raiseMinDate(delayedDate.getTime() > nextDeliveryDate.getTime() ? delayedDate : nextDeliveryDate);
        }));
      },
      error: error => console.error('Fehler beim Ermitteln des nächsten Liefertermins:', error)
    }));
  }

  /** Hebt das früheste Startdatum an (nie vor jetzt, nie zurück), passt das späteste an und belegt das Startdatum vor. */
  private raiseMinDate(date: Date) {
    const now = new Date();
    let newMinDate = date.getTime() > now.getTime() ? date : now;
    if (this.minDate().getTime() > newMinDate.getTime()) {
      newMinDate = this.minDate();
    }
    this.minDate.set(newMinDate);

    const maxDelayToStart = Number(this.configService.getConfig('order.maxDelayToStart')?.value);
    if (maxDelayToStart > 0) {
      const maxDate = new Date(newMinDate);
      maxDate.setDate(maxDate.getDate() + maxDelayToStart);
      this.maxDate.set(maxDate);
    }

    this.prefillStartDate();
  }

  /** Belegt das Startdatum mit dem frühesten Startdatum vor, solange keins oder ein zu frühes gewählt ist. */
  private prefillStartDate() {
    const startDateControl = this.paymentFormGroup?.get('startdate');
    if (!startDateControl) {
      return;
    }
    const currentStartDate = startDateControl.value ? new Date(startDateControl.value) : null;
    if (!currentStartDate || currentStartDate.getTime() < this.minDate().getTime()) {
      startDateControl.setValue(this.minDate());
    }
  }

  private buildPaymentForm(): void {
    this.paymentFormGroup = this.formBuilder.group({
      formOfPayment: ['', Validators.required],
      paymentFrequency: ['', Validators.required],
      iban: ['', Validators.required],
      bic: [{value: this.translationService.getTranslation('filled_automatically'), disabled: true}],
      accountHolder: [''], //Wird required, wenn this.paymentAccountHolder === true
      sepaOptIn: [''],
      optinFormArray: new FormArray([]),
      invoiceEmail: ['', [Validators.required, Validators.pattern(ValidationPattern.EMAIL)]],
      extendedData: new FormArray([]),
      startdate: new FormControl<Date | null>(null, Validators.required),
      enddate: [''],
      editionId: ['']
    });
  }

  protected getAdditionalDataControls(): AbstractControl[] {
    return (this.paymentFormGroup.get('extendedData') as FormArray).controls;
  }

  protected onSelectFormOfPayment(formOfPayment: FormOfPayment) {
    if (!!formOfPayment) {
      this.selectedFormOfPayment = formOfPayment;
      this.paymentFormGroup.get('formOfPayment').patchValue(formOfPayment ?? null);
    }
  }

  /**
   * transferiert die IBAN vom IBAN-Feld zum Barprämien-IBAN-Feld, um dem User den Bestellprozess zu erleichtern.
   * Funktioniert NUR in eine Richtung- es ist immer noch möglich die IBAN im Prämienfeld anzupassen
   */
  transferIban() {
    // if(this.moneyBonusSelected || this.nonMoneyBonusWithClientCoPaymentSelected) {
    //   this.paymentFormGroup.get('ibanBonus').setValue(this.paymentFormGroup.get('iban').getRawValue());
    // }
  }

  private handleVariantCodes(offer: Offer) {
    if (offer?.product?.variantCodeList.length > 0) {
      const variants = localStorage.getItem('variants_' + offer?.product?.productCode);
      if (variants && variants.length > 0) {
        // Varianten aus dem Local Storage laden, falls mit EditionResolverService geladen
        const variantArray = JSON.parse(variants);
        variantArray.forEach(_vc => {
          this.variantCodeDescriptions.set(_vc.variantCode, _vc.variantName);
        });
      } else {
        //Wenn nichts im Local Storage ist, dann selber laden
        this.offer()?.product?.variantCodeList.forEach(variantCode => {
          this.baseDataService.getVariantCode(variantCode).subscribe(baseData => {
            this.variantCodeDescriptions.set(baseData.key, baseData.description);
          });
        });
      }
    }
  }

  public calcPriceOfSubscription(): void {
    // Der Aufbau des Bestell-Objekts wirft, solange die Bestellstrecke unvollstaendig ist (z.B. noch
    // keine Zahlung im State) - das darf die Komponente nicht mitreissen.
    const order = this.safeBuildOrderDto();
    if (!order) {
      return;
    }

    this.orderTrackingService.pushEvent(GtmEnum.ADDED_PAYMENT, order);
    if(!order.email || order.email === '') {
      return;
    }

    if (this.offer().priceCalculationActivated) {
      this.priceCalculationError = false;
      this.setCalculatingPrice(true);
      this.subscriptionService.getPriceForSubscription(order).subscribe({
        next: (prices) => {
          const order = this.orderProcessService.buildOrderDto();
          this.orderTrackingService.pushEvent(GtmEnum.CALCULATED_PRICE, prices);
          this.setCalculatingPrice(false);
          const period = {
            validFrom: order.validDate.validFrom,
            validUntil: null
          };
          const calculatedPrices = [];
          prices.forEach(price => {
            if (price.trial) {
              const trialPrice = new OfferPrice(order.quantity, period, order.currency, price.price, null, true, price.porto, price.portoDescription);
              calculatedPrices.push(trialPrice);
            } else {
              if (price.previewPrice) {
                const resultPrice: OfferPrice = new OfferPrice(order.quantity, period, price.previewPrice.currency, price.previewPrice.price, this.offer().priceCalculationSummary, false, price.porto, price.portoDescription);
                calculatedPrices.push(resultPrice);
              } else if ((price.price > 0) || calculatedPrices.length == 0) {
                const resultPrice: OfferPrice = new OfferPrice(order.quantity, period, price.currency, price.price, this.offer().priceCalculationSummary, false, price.porto, price.portoDescription);
                calculatedPrices.push(resultPrice);
              }
            }
          });

          const displayPricesAsSum = this.configService.getConfig('order.price.combiPricesAsSum');
          if (displayPricesAsSum?.asBoolean() && calculatedPrices.length > 1) {
            let price: number = 0.0;
            calculatedPrices.forEach(pricePart => price = price + pricePart.price);
            calculatedPrices[0].price = price;
            this.offerPrice.set([calculatedPrices[0]]);
          } else {
            this.offerPrice.set(calculatedPrices);
          }

          this.orderProcessService.update(state => ({
            ...state,
            offerPrice: this.offerPrice()
          }));

          this.handleTransaction(this.offerPrice(), order);
          this.finishPriceCalculation();
        },
        error: (error) => {
          this.setCalculatingPrice(false);
          this.priceCalculationError = true;
          this.priceConfirmedForSelection = false;
          this.navigateWhenPriceArrives = false;
          console.error(error);
          this.emitReadyState();
        }
      });
    } else {
      //Wird der Preis nicht berechnet, dann nehmen wir den Preis aus der konfigurierten Beschreibung
      this.offerPrice.set([
        new OfferPrice(
          1, {validFrom: new Date(), validUntil: new Date()}, null, this.offer().offerPrice?.price,
          this.offer().priceCalculationSummary, false, null, null
        )
      ]);

      this.orderProcessService.update(state => ({
        ...state,
        offerPrice: this.offerPrice()
      }));

      this.handleTransaction(this.offerPrice(), order);
      this.finishPriceCalculation();
    }
  }

  /**
   * Der Zahlungs-Schritt ist erst fertig, wenn bei einer Online-Zahlart auch tatsächlich bezahlt
   * wurde - sonst gibt das "completed" des MatStep den Header des Folge-Schritts frei und der
   * gesperrte "Weiter"-Button lässt sich per Klick auf diesen Header umgehen.
   */
  protected paymentStepReady(): boolean {
    return isPaymentStepComplete(this.paymentStepState());
  }

  private paymentStepState(): PaymentStepState {
    return {
      paymentFormValid: this.paymentFormGroup.valid,
      isOnlinePayment: this.isOnlinePayment(),
      onlinePaymentSuccess: this.onlinePaymentSuccess()
    };
  }

  /**
   * Gibt der Eltern-Komponente den Schritt für die Stepper-Navigation frei. Zusätzlich zur
   * Freigabe muss der Preis berechnet sein, sofern das Angebot das verlangt - sonst führt ein Klick
   * auf den Header der Zusammenfassung dorthin, bevor dort ein Preis steht.
   */
  private paymentStepNavigable(): boolean {
    return isPaymentStepNavigable(this.paymentStepState(), this.priceConfirmedForSelection);
  }

  /** Meldet der Eltern-Komponente den Freigabe-Zustand - ausdrücklich auch dessen Entzug. */
  private emitReadyState(): void {
    this.readyState.emit(this.paymentStepNavigable());
  }

  protected onNextClick(): void {
    if (!this.paymentStepReady()) {
      return;
    }

    this.calcPriceOfSubscription();

    // Läuft die Preisberechnung noch, wird erst weitergesteppt, wenn ihr Ergebnis da ist
    // (s. finishPriceCalculation) - sonst steht auf der Zusammenfassung noch kein Preis.
    if (!this.paymentStepNavigable()) {
      this.navigateWhenPriceArrives = true;
      return;
    }

    this.completeAndNavigate();
  }

  /** Markiert den Schritt als erledigt und steppt weiter. */
  private completeAndNavigate(): void {
    this.emitReadyState();
    if (this.parentStepper?.selected) {
      this.parentStepper.selected.completed = true;
    }

    // Wait one tick so parent completion bindings can settle before linear-step navigation.
    setTimeout(() => this.parentStepper?.next());
  }

  /**
   * Schließt die Preisberechnung ab: meldet den neuen Freigabe-Zustand und holt die Navigation
   * nach, die auf das Ergebnis gewartet hat.
   */
  private finishPriceCalculation(): void {
    this.priceConfirmedForSelection = this.offerPrice()?.length > 0;
    this.emitReadyState();

    if (this.navigateWhenPriceArrives && this.paymentStepNavigable()) {
      this.navigateWhenPriceArrives = false;
      this.completeAndNavigate();
    }
  }

  /**
   * Transactionsdaten für PSA sichern, diese werden u. A. für Abrechnungszwecke benutzt
   * @param calculatedPrices
   * @param order
   * @private
   */
  private handleTransaction(calculatedPrices: any[], order?: any) {
    if (!Array.isArray(calculatedPrices) || calculatedPrices.length === 0) {
      return;
    }
    order = order ?? this.safeBuildOrderDto();
    const transaction = new PaymentTransactionModel();
    transaction.amount = calculatedPrices[0]?.price;

    // Sichern der Webabo-Transaktionsdaten für die Beschreibung (Bestellübersicht) im PayPal:
    // so können die Daten ggf. der Bestellung zugeordnet werden.
    const trackingTransactionId = this.orderTrackingService.getTransactionId();
    const ssoId = this.userData()?.userSSOId;
    transaction.invoiceText = this.OFFER_ID_KEY + this.offer()?.recno
      + this.SSO_ID_KEY + (ssoId > 0 ? ssoId : '0')
      + this.WEBABO_TRANSACTION_ID_KEY + (trackingTransactionId ?? '-');

    const paymentCode = order?.payment?.paymentType?.paymentMethod ?? this.selectedFormOfPayment?.paymentMethod;
    const paypalPaymentCode = this.configService.getConfig('paymentprovider.paypal.paymentcode');
    const buckarooPaymentCode = this.configService.getConfig('paymentprovider.buckaroo.paymentcode');

    if (paymentCode === paypalPaymentCode?.value) {
      transaction.provider = PaymentProvider.PAYPAL;
      transaction.billingPlanId = this.offer().offerPlan?.paymentPlans?.filter(pp => pp.paymentProvider.toString() === 'PAYPAL')[0]?.externalPlanId;
      if (transaction.billingPlanId) {
        transaction.amount = "0";
      }
      this.isOnlinePayment.set(true);
    } else if (paymentCode == buckarooPaymentCode?.value) {
      transaction.provider = PaymentProvider.BUCKAROO;
      transaction.orderObject = order ?? this.safeBuildOrderDto();
      this.isOnlinePayment.set(true);
    }
    if (this.isOnlinePayment()) {
      this.paymentTransactionService.setNewTransaction(transaction);
    }
  }

  private handleFormsOfPayment(offer: Offer) {
    offer.offerPaymentOfferType?.forEach(formOfPayment => {
      const foundFormOfPayment = this.allFormsOfPayment.find(searchFormOfPayment => searchFormOfPayment.key === formOfPayment.paymentMethod);
      if (foundFormOfPayment && foundFormOfPayment.useInFE && formOfPayment.paymentFrequencyList?.length > 0) {
        this.imageService.getImagePaymentType(formOfPayment.paymentMethod).subscribe(image => {
          if (image && image.type) {
            formOfPayment.image = this.imageHandler.addAltText(image, formOfPayment.description ?? this.translationService.getTranslation('paymentMethod.image.altText.default'));
          }
        });
        formOfPayment.description = foundFormOfPayment.description;
        //TODO wieder ausbauen wenn die richtige Lösung da ist!
        const newDescription = this.translationService.getTranslation('paymentcode.' + foundFormOfPayment.key);
        if (newDescription && newDescription !== '') {
          formOfPayment.description = newDescription;
        }
        // der Invoice-Typ muss leider separat ermittelt werden
        this.paymentService.getPaymentMethodByCode(formOfPayment?.paymentMethod).subscribe($paymentMethod => {
          formOfPayment.invoiceType = $paymentMethod.invoiceType;
          // invoiceType wird asynchron geladen. Ist diese Zahlart bereits (automatisch) ausgewählt
          // - z.B. bei Angeboten mit nur einer Zahlart wie "Rechnung per E-Mail" -, dann wurde die
          // Rechnungs-E-Mail-Abfrage bereits ohne invoiceType ausgewertet und muss nun nachgezogen
          // werden, sonst fehlt das E-Mail-Feld.
          if (this.selectedFormOfPayment?.paymentMethod === formOfPayment.paymentMethod) {
            this.applyInvoiceEmailRequirement(formOfPayment);
          }
        });
        this.formsOfPayment.push(formOfPayment);
      }
    });
  }

  private handleFormOfPaymentChange(value) {
    this.paymentFrequencies = [];
    this.onlinePaymentSuccess.set(false);
    this.autoNavigatedAfterOnlinePayment = false;
    // Der bisher ermittelte Preis gehörte zur alten Zahlart und gilt nicht mehr.
    this.priceConfirmedForSelection = false;
    this.navigateWhenPriceArrives = false;
    const findFormOfPayment = this.formsOfPayment?.find(formOfPayment => formOfPayment.paymentMethod === value.paymentMethod);

    // Hängt an der Zahlart, nicht am Zahlrhythmus - deshalb einmalig vor der Frequenz-Schleife und
    // nicht darin (sonst bliebe der Zustand bei leerer paymentFrequencyList auf dem alten Wert).
    const isOnlinePayment = isOnlinePaymentMethod(findFormOfPayment, this.onlinePaymentCodes());
    this.onlinePaymentSelected.emit(isOnlinePayment);
    this.isOnlinePayment.set(isOnlinePayment);

    findFormOfPayment?.paymentFrequencyList?.forEach(searchFrequency => {
      const foundFrequency = this.allPaymentFrequencies.find(frequency => frequency.key === searchFrequency);
      if (foundFrequency) {
        this.setValidatorsAtIBAN(findFormOfPayment.paymentType === 'DIRECTDEBITIBAN', false);
        this.applyInvoiceEmailRequirement(findFormOfPayment);
        this.paymentFrequencies.push(foundFrequency);
      }
    });
    this.selectedFormOfPayment = value;
    if (this.paymentFrequencies.length === 1) {
      this.paymentFormGroup.get('paymentFrequency').setValue(this.paymentFrequencies[0]);
    }

    this.prefillStartDate();
    this.paymentFormGroup.get('editionId')

    // erste Ausgabe vorbelegen, bis etwas anderes entschieden wird
    this.paymentFormGroup.get('editionId')?.setValue( this.offer()?.product?.variantCodeList?.[0] ?? null );

    this.prepareOnlinePaymentTransaction();

    // Bei einer Online-Zahlart muss der Preis sofort ermittelt werden: Der Bezahl-Button wird mit
    // diesem Betrag aufgebaut, und der "Weiter"-Button ist bis zur Zahlung gesperrt - er kann die
    // Berechnung also nicht mehr anstossen. Ohne das bliebe es beim Ersatzpreis aus
    // prepareOnlinePaymentTransaction, und der Kunde wuerde mit dem falschen Betrag belastet.
    if (this.isOnlinePayment()) {
      this.calcPriceOfSubscription();
    }

    // Zahlartwechsel verwirft eine ggf. schon erfolgte Online-Zahlung (s.o.) - die Freigabe des
    // Steps muss deshalb neu bewertet und notfalls wieder entzogen werden.
    this.emitReadyState();
  }

  /** Die konfigurierten Zahlart-Codes der Online-Anbieter - dieselbe Quelle wie handleTransaction. */
  private onlinePaymentCodes(): OnlinePaymentCodes {
    return {
      paypal: this.configService.getConfig('paymentprovider.paypal.paymentcode')?.value,
      buckaroo: this.configService.getConfig('paymentprovider.buckaroo.paymentcode')?.value
    };
  }

  private prepareOnlinePaymentTransaction(): void {
    if (!this.isOnlinePayment()) {
      return;
    }

    const currentPrice = this.offerPrice()?.[0];
    const fallbackPrice = currentPrice?.price ?? this.offer()?.offerPrice?.price ?? 0;
    const fallbackCurrency = currentPrice?.currency ?? 'EUR';
    const transactionPrice = [
      new OfferPrice(
        1,
        {validFrom: new Date(), validUntil: null},
        fallbackCurrency,
        fallbackPrice,
        this.offer()?.priceCalculationSummary,
        false,
        null,
        null
      )
    ];

    // Let form state updates settle before creating the transaction payload.
    setTimeout(() => this.handleTransaction(transactionPrice, null), 0);
  }

  private setCalculatingPrice(value: boolean): void {
    this.calculatingPrice = value;
    this.orderProcessService.update(state => ({
      ...state,
      calculatingPrice: value
    }));
  }

  private safeBuildOrderDto(): any {
    try {
      return this.orderProcessService.buildOrderDto();
    } catch (e) {
      console.debug('Bestell-Objekt noch nicht aufbaubar - Bestellstrecke ist noch unvollständig', e);
      return null;
    }
  }

  setUniquePaymentInfos(): void {
    if (this.formsOfPayment.length !== 1) {
      return;
    }

    // Nur eine Zahlart -> automatisch auswählen, als hätte der Nutzer die Zahlart-Kachel angeklickt.
    // Läuft über denselben Pfad wie der Klick (patchValue -> valueChanges -> handleFormOfPaymentChange),
    // damit alle nötigen Felder (IBAN/BIC, Zahlrhythmus, Rechnungs-E-Mail) gesetzt werden.
    this.onSelectFormOfPayment(this.formsOfPayment[0]);
  }

  setValidatorsAtIBAN(activate: boolean, forMoneyBonus: boolean) {
    let ibanField = 'iban';
    let bicField = 'bic';
    let sepaOptIn = 'sepaOptIn';
    let accountHolder = 'accountHolder';

    if (forMoneyBonus) {
      const bonus = 'Bonus';
      ibanField = ibanField.concat(bonus);
      bicField = bicField.concat(bonus);
      accountHolder = accountHolder.concat(bonus);
    }
    if (activate) {
      this.paymentFormGroup.get(ibanField).enable();
      this.paymentFormGroup.get(ibanField).setValidators(Validators.required);
      this.paymentFormGroup.get(bicField).setValidators(Validators.required);

      if (this.askForPaymentAccountHolder) {
        this.paymentFormGroup.get(accountHolder).setValidators(Validators.required);
      }

      this.paymentFormGroup.get(sepaOptIn).enable();

      if (this.showSEPABox()) {
        this.paymentFormGroup.get(sepaOptIn).setValidators(Validators.required);
      }
      this.setAsyncValidators();
      if (!forMoneyBonus) {
        this.needIbanBic = true;
      }
    } else {
      this.paymentFormGroup.get(ibanField).clearValidators();
      this.paymentFormGroup.get(ibanField).clearAsyncValidators();
      this.paymentFormGroup.get(bicField).clearValidators();
      this.paymentFormGroup.get(accountHolder).clearValidators();
      this.paymentFormGroup.get(sepaOptIn).clearValidators();

      this.paymentFormGroup.get(sepaOptIn).disable();
      this.paymentFormGroup.get(ibanField).reset();
      this.paymentFormGroup.get(accountHolder).reset();
      this.paymentFormGroup.get(ibanField).disable();
      if (!forMoneyBonus) {
        this.needIbanBic = false;
        this.toggleBic();
      }
    }
  }

  onCardClick(formOfPayment: FormOfPayment): void {
    if (formOfPayment) {
      this.paymentFormGroup.get('formOfPayment').patchValue(formOfPayment);
    } else {
      this.paymentFormGroup.get('formOfPayment').patchValue(null);
    }
  }

  setAsyncValidators(): void {
    this.paymentFormGroup.get('iban').setAsyncValidators(CustomValidators.validateIBAN(
      this.http,
      this.translationService,
      this.paymentFormGroup.get('bic')
    ));
  }

  /**
   * Wertet anhand der (ggf. asynchron geladenen) invoiceType der Zahlart aus, ob eine
   * Rechnungs-E-Mail abgefragt werden muss, setzt die Validatoren und belegt das Feld bei Bedarf
   * mit der bereits erfassten E-Mail vor. Idempotent - kann nach dem Laden des invoiceType erneut
   * aufgerufen werden, ohne eine vom Nutzer eingegebene E-Mail zu überschreiben.
   */
  applyInvoiceEmailRequirement(formOfPayment): void {
    const needInvoiceEmail = formOfPayment?.invoiceType === 'EMAILDELIVERY';
    this.setValidatorsAtInvoiceEmail(needInvoiceEmail);
    if (needInvoiceEmail && !this.paymentFormGroup.get('invoiceEmail').value) {
      const snapshot = this.orderProcessService.snapshot();
      // Registrierungs-E-Mail, sonst SSO-/Hasso-Login-E-Mail (eingeloggt = kein Registrierungs-Schritt).
      const email = snapshot?.registration?.email || snapshot?.ssoEmail;
      this.paymentFormGroup.get('invoiceEmail').patchValue(email);
      this.lastSyncedRegistrationEmail = email;
    }
  }

  setValidatorsAtInvoiceEmail(activate: boolean) {
    const invoiceEmail = this.paymentFormGroup.get('invoiceEmail');
    if (activate) {
      invoiceEmail.setValidators([Validators.required, Validators.pattern(ValidationPattern.EMAIL)]);
      this.needInvoiceMailAddress.set(true);
    } else {
      invoiceEmail.clearValidators();
      invoiceEmail.reset();
      this.needInvoiceMailAddress.set(false);
    }
    // Validität sofort neu berechnen, da die Validatoren ggf. erst nach dem asynchronen
    // Laden des invoiceType gesetzt werden.
    invoiceEmail.updateValueAndValidity();
  }

  toggleBic() {
    if (!this.needIbanBic) {
      this.paymentFormGroup.get('bic')?.setValue(null);
      this.paymentFormGroup.get('bic')?.markAsPristine();
      this.paymentFormGroup.get('bic')?.markAsUntouched();
    }
  }

  protected setOnlinePaymentSuccess($event: boolean) {
    this.onlinePaymentSuccess.set($event);

    // Der Zahlungsanbieter meldet den Erfolg hier - und nur hier. Der Bestell-State trägt die
    // Information weiter, damit die Offer-Card den Bestell-Button nicht aus den Feldern der
    // Transaktion erraten muss (Order- und Subscription-Zahlungen füllen verschiedene Felder).
    this.orderProcessService.update(state => ({
      ...state,
      onlinePaymentFinished: $event
    }));

    this.emitReadyState();
    if ($event) {
      this.syncPriceFromOnlineTransaction();
    }
    if ($event && !this.autoNavigatedAfterOnlinePayment) {
      this.autoNavigatedAfterOnlinePayment = true;
      setTimeout(() => this.onNextClick(), 0);
    }
  }

  private syncPriceFromOnlineTransaction(): void {
    const transaction = this.paymentTransactionService.getValue();
    const amount = Number(transaction?.amount ?? this.offer()?.offerPrice?.price ?? 0);
    if (!Number.isFinite(amount) || amount < 0) {
      return;
    }

    const prices = [
      new OfferPrice(
        1,
        {validFrom: new Date(), validUntil: null},
        this.payPalCurrencyFallback(),
        amount,
        this.offer()?.priceCalculationSummary,
        false,
        null,
        null
      )
    ];

    this.offerPrice.set(prices);
    this.orderProcessService.update(state => ({
      ...state,
      offerPrice: prices
    }));
  }

  private payPalCurrencyFallback(): string {
    return this.offerPrice()?.[0]?.currency ?? 'EUR';
  }

  private skipDirectlyToLastStep(attempt: number = 0): void {
    const stepper = this.parentStepper;
    const steps = stepper?.steps?.toArray?.() ?? [];
    if (!stepper || steps.length === 0) {
      if (attempt < 10) {
        setTimeout(() => this.skipDirectlyToLastStep(attempt + 1), 50);
      }
      return;
    }

    this.readyState.emit(true);
    if (stepper.selected) {
      stepper.selected.completed = true;
    }

    setTimeout(() => {
      stepper.selectedIndex = Math.max(steps.length - 1, 0);
    }, 0);
  }
}
